// Admin → Dashboard: one page, two tabs.
//
// Overview and Usage used to be separate nav items showing halves of the same
// question ("what is this workspace doing right now?"), and System sat beside
// them as a third. They are one page now: headline counts, then the trend that
// explains them, with System a tab away rather than a navigation away.
//
// Charts follow the same rules everywhere here: colour is IDENTITY (a series
// keeps its colour whatever the numbers do), one shared axis for same-unit
// series, and every plot answers a hover.

import { memo, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { AdminOverview, api, ComponentHealth, SfuMetrics, SfuSeries, Usage } from '../api';
import { KpiCard, trendOf } from './UsageDashboard';
import { SeriesDot, TrendChart, TrendSeries } from './TrendChart';

/**
 * Series colours, checked rather than chosen by eye: every pair is ≥ 23 ΔE
 * apart in OKLab for normal vision, and the closest pair under simulated
 * protanopia/deuteranopia is 10 ΔE (the floor for "still telling apart" is 8).
 * Lightness varies as well as hue, which is what survives colour blindness.
 *
 * Dropped packets deliberately take the warm/warning hue — there the colour is
 * carrying meaning, not just identity.
 */
const SERIES_COLORS: Record<string, string> = {
  meetings: '#0055ff',
  calls: '#12a594',
  rooms: '#0055ff',
  participants: '#12a594',
  quality: '#c026a8',
  pkts_out: '#4b5563',
  pkts_dropped: '#e08600',
};
const FALLBACK_COLOR = '#0055ff';

const PERIODS = [
  { key: 'daily', label: 'Daily', days: 1 },
  { key: 'weekly', label: 'Weekly', days: 7 },
  { key: 'monthly', label: 'Monthly', days: 30 },
  { key: 'yearly', label: 'Yearly', days: 365 },
] as const;
type Period = (typeof PERIODS)[number]['key'];

const SYS_RANGES = [
  { key: '15m', label: '15m', min: 15 },
  { key: '1h', label: '1h', min: 60 },
  { key: '6h', label: '6h', min: 360 },
  { key: '24h', label: '24h', min: 1440 },
] as const;
type SysRange = (typeof SYS_RANGES)[number]['key'];

export type DashTab = 'overview' | 'system';

export function AdminDashboard({ onNavigate }: { onNavigate: (s: string) => void }) {
  const [tab, setTab] = useState<DashTab>('overview');
  const showOverview = useCallback(() => setTab('overview'), []);
  const showSystem = useCallback(() => setTab('system'), []);

  return (
    <section>
      <div className="bz-cseg bz-dash-tabs" role="tablist" aria-label="Dashboard">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'overview'}
          className={tab === 'overview' ? 'bz-cseg-btn bz-cseg-btn-on' : 'bz-cseg-btn'}
          onClick={showOverview}
        >
          Overview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'system'}
          className={tab === 'system' ? 'bz-cseg-btn bz-cseg-btn-on' : 'bz-cseg-btn'}
          onClick={showSystem}
        >
          System
        </button>
      </div>

      {tab === 'overview' ? <OverviewTab onNavigate={onNavigate} /> : <SystemTab />}
    </section>
  );
}

// ── Overview: the headline counts, then the trend behind them ───────────────

function OverviewTab({ onNavigate }: { onNavigate: (s: string) => void }) {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [period, setPeriod] = useState<Period>('monthly');
  const [usage, setUsage] = useState<Usage | null>(null);

  useEffect(() => {
    api.adminOverview().then(setOverview).catch(() => setOverview(null));
  }, []);

  const days = PERIODS.find((p) => p.key === period)!.days;
  useEffect(() => {
    // The previous window stays on screen while the new one loads: a dashboard
    // that blanks out on every click reads as broken.
    api.adminUsage(days).then(setUsage).catch(() => undefined);
  }, [days]);

  const onPeriod = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    setPeriod(e.currentTarget.dataset.period as Period);
  }, []);

  const goUsers = useCallback(() => onNavigate('users'), [onNavigate]);
  const goLive = useCallback(() => onNavigate('live'), [onNavigate]);
  const goHistory = useCallback(() => onNavigate('history'), [onNavigate]);

  // Derived once per usage payload: these feed both the cards and the chart,
  // and a fresh array each render would defeat every memo below them.
  const { meetings, calls, durations, labels } = useMemo(
    () => ({
      meetings: usage?.daily.map((d) => d.meetings) ?? [],
      calls: usage?.daily.map((d) => d.calls ?? 0) ?? [],
      durations: usage?.daily.map((d) => Math.round(d.avgDurationSec / 60)) ?? [],
      labels: usage?.daily.map((d) => shortDate(d.date)) ?? [],
    }),
    [usage],
  );

  const series: TrendSeries[] = useMemo(
    () => [
      { key: 'meetings', label: 'Meetings', color: SERIES_COLORS.meetings!, values: meetings },
      { key: 'calls', label: 'Calls', color: SERIES_COLORS.calls!, values: calls },
    ],
    [meetings, calls],
  );

  return (
    <>
      <div className="bz-kpi2-grid">
        <OverviewCard
          icon={ICON_USERS}
          title="Total users"
          value={overview ? overview.distinctUsers.toLocaleString() : '—'}
          tone="green"
          subLabel="Admissions (24h)"
          subValue={overview ? overview.admissionsLast24h.toLocaleString() : '—'}
          onDetails={goUsers}
        />
        <OverviewCard
          icon={ICON_VIDEO}
          title="Total meetings"
          value={overview ? overview.totalMeetings.toLocaleString() : '—'}
          tone="blue"
          subLabel="Live now"
          subValue={overview ? overview.activeMeetings.toLocaleString() : '—'}
          onDetails={goLive}
        />
        <OverviewCard
          icon={ICON_ACTIVITY}
          title="Total sessions"
          value={overview ? overview.totalSessions.toLocaleString() : '—'}
          tone="amber"
          subLabel="Tokens issued (24h)"
          subValue={overview ? overview.tokensLast24h.toLocaleString() : '—'}
          onDetails={goHistory}
        />
        <OverviewCard
          icon={ICON_PHONE}
          title="Calls"
          value={overview ? overview.totalCalls.toLocaleString() : '—'}
          tone="violet"
          subLabel="Live now"
          subValue={overview ? overview.activeCalls.toLocaleString() : '—'}
        />
      </div>

      <div className="bz-dash-rule" />

      <div className="bz-dash-periods">
        <div className="bz-cseg" role="tablist" aria-label="Period">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={period === p.key}
              data-period={p.key}
              className={period === p.key ? 'bz-cseg-btn bz-cseg-btn-on' : 'bz-cseg-btn'}
              onClick={onPeriod}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bz-kpi-grid">
        <KpiCard
          label="Meetings"
          value={usage ? usage.meetingsInWindow.toLocaleString() : '—'}
          series={meetings}
          trend={trendOf(meetings)}
        />
        <KpiCard
          label="Avg duration"
          value={usage ? Math.round(usage.avgDurationSec / 60) : '—'}
          unit="min"
          series={durations}
          trend={trendOf(durations)}
        />
        <KpiCard
          label="Peak concurrency"
          value={usage ? usage.peakConcurrency.toLocaleString() : '—'}
          series={meetings}
          trend={trendOf(meetings)}
        />
        <KpiCard
          label="Calls"
          value={usage ? (usage.callsInWindow ?? 0).toLocaleString() : '—'}
          series={calls}
          trend={trendOf(calls)}
        />
      </div>

      <div className="bz-dash-chart">
        <div className="bz-dash-chart-head">
          <h3 className="bz-dash-chart-title">Activity</h3>
          <div className="bz-dash-legend">
            {series.map((s) => (
              <span key={s.key} className="bz-dash-legend-item">
                <SeriesDot color={s.color} />
                {s.label}
              </span>
            ))}
          </div>
        </div>
        {labels.length > 1 ? (
          <TrendChart labels={labels} series={series} />
        ) : (
          <p className="bz-cnote">Not enough activity in this period to draw a trend yet.</p>
        )}
      </div>
    </>
  );
}

// ── System: health + the SFU series, one colour each ────────────────────────

function SystemTab() {
  const [range, setRange] = useState<SysRange>('1h');
  const [m, setM] = useState<SfuMetrics | null>(null);
  const [health, setHealth] = useState<ComponentHealth[]>([]);

  const rangeMin = SYS_RANGES.find((r) => r.key === range)!.min;
  useEffect(() => {
    const load = () => api.adminSFU(rangeMin).then(setM).catch(() => setM({ enabled: false }));
    load();
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, [rangeMin]);
  useEffect(() => {
    const load = () => api.adminHealth().then(setHealth).catch(() => setHealth([]));
    load();
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, []);

  const onRange = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    setRange(e.currentTarget.dataset.range as SysRange);
  }, []);

  return (
    <>
      <div className="bz-sys-head">
        <div className="bz-sys-health">
          {health.map((h) => (
            <span
              key={h.name}
              className={h.ok ? 'bz-sys-chip bz-sys-chip-ok' : 'bz-sys-chip bz-sys-chip-down'}
              title={h.error ?? ''}
            >
              <span className="bz-sys-chip-dot" />
              {h.name}
            </span>
          ))}
        </div>
        <div className="bz-cseg" role="tablist" aria-label="Range">
          {SYS_RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              role="tab"
              aria-selected={range === r.key}
              data-range={r.key}
              className={range === r.key ? 'bz-cseg-btn bz-cseg-btn-on' : 'bz-cseg-btn'}
              onClick={onRange}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {m && !m.enabled ? (
        <p className="bz-cnote">
          Live SFU metrics require Prometheus, which isn&rsquo;t configured in this environment. The
          component health above covers up/down status.
        </p>
      ) : (
        <div className="bz-sys-grid">
          {(m?.series ?? []).map((sr) => (
            <SystemCard key={sr.key} series={sr} />
          ))}
        </div>
      )}
    </>
  );
}

/** One SFU series: its own colour, its own chart, its own memo boundary — a
 * 15-second refresh must not repaint five charts when one changed. */
const SystemCard = memo(function SystemCard({ series }: { series: SfuSeries }) {
  const color = SERIES_COLORS[series.key] ?? FALLBACK_COLOR;
  const { labels, values, chart } = useMemo(() => {
    const values = series.points.map((p) => Math.round(p.v * 100) / 100);
    return {
      values,
      labels: series.points.map((p) => fmtClock(p.t)),
      chart: [{ key: series.key, label: series.title, color, values }],
    };
  }, [series, color]);
  const latest = values.length ? values[values.length - 1]! : 0;

  return (
    <div className="bz-sys-card">
      <div className="bz-sys-card-head">
        <span className="bz-sys-card-title">
          <SeriesDot color={color} className="bz-sys-card-dot" />
          {series.title}
        </span>
        <span className="bz-sys-card-val">
          {latest.toLocaleString()}
          {series.unit && <span className="bz-sys-card-unit"> {series.unit}</span>}
        </span>
      </div>
      {values.length > 1 ? (
        <TrendChart size="compact" labels={labels} series={chart} />
      ) : (
        <p className="bz-cnote bz-sys-empty">No samples in this range.</p>
      )}
    </div>
  );
});

// ── bits ────────────────────────────────────────────────────────────────────

/** "2026-08-19" → "Aug 19", which is what a person reads on an axis. */
function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const fmtClock = (t: number) => {
  const d = new Date(t * 1000);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const OverviewCard = memo(function OverviewCard({
  icon,
  title,
  value,
  tone,
  subLabel,
  subValue,
  onDetails,
}: {
  icon: React.ReactElement;
  title: string;
  value: string;
  /** 'calls' matches the Calls series colour used by the chart below. */
  tone: 'green' | 'blue' | 'amber' | 'violet' | 'red' | 'muted';
  subLabel: string;
  subValue: string;
  /** Omitted when there is no deeper page to go to — no dead affordances. */
  onDetails?: () => void;
}) {
  return (
    <div className={onDetails ? 'bz-kpi2' : 'bz-kpi2 bz-kpi2-nofoot'}>
      <div className="bz-kpi2-island">
        <div className="bz-kpi2-head">
          <span className="bz-kpi2-icon">{icon}</span>
          <div className="bz-kpi2-headtext">
            <div className="bz-kpi2-title">{title}</div>
            <div className="bz-kpi2-value">{value}</div>
          </div>
        </div>
        <div className={`bz-kpi2-row t-${tone}`}>
          <span className="bz-kpi2-row-label">
            <span className="bz-kpi2-dot" />
            {subLabel}
          </span>
          <span className="bz-kpi2-row-val">{subValue}</span>
        </div>
      </div>
      {onDetails && (
        <button type="button" className="bz-kpi2-foot" onClick={onDetails}>
          See in details
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      )}
    </div>
  );
});

const ICON_USERS = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const ICON_VIDEO = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 7l-7 5 7 5V7z" />
    <rect x="1" y="5" width="15" height="14" rx="2" />
  </svg>
);
const ICON_ACTIVITY = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18M9 21V9" />
  </svg>
);
const ICON_PHONE = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
